import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import { ClientPortalBadge, formatMoney } from "../clientPortalUtils";

const EMPTY_SUMMARY = {
  activeClients: 0,
  activeProjects: 0,
  openRequests: 0,
  pendingApprovals: 0,
  sharedDocuments: 0,
  outstandingInvoices: 0,
};

function SummaryMetric({ label, value, tone, money }) {
  const accent =
    tone === "success" ? "var(--success)" :
    tone === "danger" ? "var(--danger)" :
    tone === "warn" ? "var(--warn)" :
    "#7c3aed";

  return (
    <div className="summary-card" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="summary-card-label">{label}</div>
      <div className="summary-card-value" style={{ color: accent }}>
        {money ? formatMoney(value) : value}
      </div>
    </div>
  );
}

export default function ClientPortalDashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [requests, setRequests] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryRes, requestsRes, approvalsRes, invoicesRes, projectsRes] = await Promise.all([
        apiFetch("/api/client-portal/summary"),
        apiFetch("/api/client-portal/requests"),
        apiFetch("/api/client-portal/approvals?status=Pending"),
        apiFetch("/api/client-portal/invoices"),
        apiFetch("/api/client-portal/projects"),
      ]);

      if (!summaryRes.ok) throw new Error(await summaryRes.text());
      if (!requestsRes.ok) throw new Error(await requestsRes.text());
      if (!approvalsRes.ok) throw new Error(await approvalsRes.text());
      if (!invoicesRes.ok) throw new Error(await invoicesRes.text());
      if (!projectsRes.ok) throw new Error(await projectsRes.text());

      setSummary(await summaryRes.json());
      setRequests(await requestsRes.json());
      setApprovals(await approvalsRes.json());
      setInvoices(await invoicesRes.json());
      setProjects(await projectsRes.json());
    } catch (err) {
      setError(err?.message || "Could not load client portal dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const projectProgress = projects.length > 0
    ? Math.round(projects.reduce((sum, item) => sum + (item.progressPercent || 0), 0) / projects.length)
    : 0;

  return (
    <div className="client-portal-page">
      <PageHeader
        title="Client Portal"
        subtitle="Client accounts, project visibility, service requests, approvals, documents, and invoices."
        actions={
          <button className="primary" onClick={() => navigate("/client-portal/clients")}>
            Add Client
          </button>
        }
      />

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <Loader.SummaryCards />
      ) : (
        <div className="summary-grid">
          <SummaryMetric label="Active Clients" value={summary.activeClients} />
          <SummaryMetric label="Active Projects" value={summary.activeProjects} tone="success" />
          <SummaryMetric label="Open Requests" value={summary.openRequests} tone="warn" />
          <SummaryMetric label="Pending Approvals" value={summary.pendingApprovals} tone="danger" />
          <SummaryMetric label="Documents Shared" value={summary.sharedDocuments} tone="success" />
          <SummaryMetric label="Outstanding" value={summary.outstandingInvoices} money tone="warn" />
        </div>
      )}

      <div className="client-portal-dashboard-grid">
        <section className="glass-card client-portal-panel">
          <div className="client-portal-panel-header">
            <div>
              <h3>Project Delivery</h3>
              <p>{projects.length} client project{projects.length === 1 ? "" : "s"} linked to portal accounts.</p>
            </div>
            <span className="client-portal-percent">{projectProgress}%</span>
          </div>
          <div className="client-portal-progress-wrap">
            <div className="client-portal-progress">
              <span style={{ width: `${projectProgress}%` }} />
            </div>
          </div>
        </section>

        <section className="glass-card client-portal-panel">
          <div className="client-portal-panel-header">
            <div>
              <h3>Client Requests</h3>
              <p>Latest requests raised from client accounts.</p>
            </div>
            <button className="btn-ghost" onClick={() => navigate("/client-portal/requests")}>Open</button>
          </div>

          {loading ? (
            <div className="client-portal-list-skeleton">
              {[1, 2, 3].map((item) => <Loader key={item} type="skeleton" height={42} />)}
            </div>
          ) : requests.length === 0 ? (
            <EmptyState title="No requests yet" subtitle="Client requests will appear here once submitted." />
          ) : (
            <div className="client-portal-compact-list">
              {requests.slice(0, 5).map((request) => (
                <div key={request.id} className="client-portal-compact-row">
                  <div>
                    <div className="client-portal-row-title">{request.title}</div>
                    <div className="client-portal-row-subtitle">{request.clientName} - {request.category}</div>
                  </div>
                  <ClientPortalBadge status={request.status} />
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="glass-card client-portal-panel">
          <div className="client-portal-panel-header">
            <div>
              <h3>Pending Approvals</h3>
              <p>Deliverables and sign-offs awaiting client response.</p>
            </div>
            <button className="btn-ghost" onClick={() => navigate("/client-portal/approvals")}>Review</button>
          </div>

          {loading ? (
            <div className="client-portal-list-skeleton">
              {[1, 2, 3].map((item) => <Loader key={item} type="skeleton" height={42} />)}
            </div>
          ) : approvals.length === 0 ? (
            <EmptyState title="No pending approvals" subtitle="Approval requests are clear for now." />
          ) : (
            <div className="client-portal-compact-list">
              {approvals.slice(0, 5).map((approval) => (
                <div key={approval.id} className="client-portal-compact-row">
                  <div>
                    <div className="client-portal-row-title">{approval.title}</div>
                    <div className="client-portal-row-subtitle">{approval.clientName} - due {formatDate(approval.dueDate)}</div>
                  </div>
                  <ClientPortalBadge status={approval.status} />
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="glass-card client-portal-panel">
          <div className="client-portal-panel-header">
            <div>
              <h3>Client Invoices</h3>
              <p>Billing records visible through client relationships.</p>
            </div>
            <button className="btn-ghost" onClick={() => navigate("/client-portal/invoices")}>View</button>
          </div>

          {loading ? (
            <div className="client-portal-list-skeleton">
              {[1, 2, 3].map((item) => <Loader key={item} type="skeleton" height={42} />)}
            </div>
          ) : invoices.length === 0 ? (
            <EmptyState title="No invoices linked" subtitle="Finance invoices linked to clients appear here." />
          ) : (
            <div className="client-portal-compact-list">
              {invoices.slice(0, 5).map((invoice) => (
                <div key={invoice.id} className="client-portal-compact-row">
                  <div>
                    <div className="client-portal-row-title">{invoice.clientName}</div>
                    <div className="client-portal-row-subtitle">{invoice.invoiceNumber} - due {formatDate(invoice.dueDate)}</div>
                  </div>
                  <div className="client-portal-row-right">
                    <strong>{formatMoney(invoice.totalAmount, invoice.currency)}</strong>
                    <ClientPortalBadge status={invoice.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
